import { useState, useEffect, useMemo, useCallback, useRef, memo } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { getPlans } from "../../services/stripeService";
import MemberCard from "./MemberCard";

// ─── Palette ──────────────────────────────────────────────────────────────────

const BRAND     = "#A259FF";
const TEXT_PRI  = "rgba(255,255,255,0.90)";
const TEXT_SEC  = "rgba(255,255,255,0.60)";
const TEXT_TER  = "rgba(255,255,255,0.35)";
const BORDER    = "rgba(255,255,255,0.08)";
const CARD_BG   = "rgba(255,255,255,0.04)";
const INPUT_BG  = "rgba(255,255,255,0.06)";
const HOVER_BG  = "rgba(255,255,255,0.05)";

const SIDEBAR_W = 200;

// ─── Status ───────────────────────────────────────────────────────────────────

const STATUS_COLOR = {
  online:  "#23A55A",
  idle:    "#F0B232",
  dnd:     "#F23F43",
  offline: "#3a3a3a",
};

// ─── Plan grouping helpers ────────────────────────────────────────────────────

const FALLBACK_PLAN_ORDER = ["ultimate", "pro", "basic", "free"];
const PLAN_TIER_COLORS    = ["#F0B232", "#A259FF", "#60a5fa", "#80848E"];

function planTierColor(plan, planOrder) {
  const idx = planOrder.indexOf(plan);
  if (idx === -1) return PLAN_TIER_COLORS[PLAN_TIER_COLORS.length - 1];
  return PLAN_TIER_COLORS[Math.min(idx, PLAN_TIER_COLORS.length - 1)];
}

function planKey(member) {
  const { subscription } = member;
  if (subscription?.subscriptionStatus !== "active") return "free";
  return (subscription.planType || "free").toLowerCase();
}

function planLabel(plan, count, plansMap) {
  const name = plansMap[plan]?.name?.toUpperCase() ?? plan.toUpperCase();
  return `${name} — ${count}`;
}

function groupMembers(flatMembers, planOrder, plansMap) {
  const buckets = {};
  for (const m of flatMembers) {
    const plan = planKey(m);
    if (!buckets[plan]) buckets[plan] = [];
    buckets[plan].push({
      id: m.id,
      name: m.displayName || m.username || m.id,
      username: m.username || null,
      joinedAt: m.createdAt ?? m.joinedAt ?? null,
      plan,
      planName: plansMap[plan]?.name ?? plan.charAt(0).toUpperCase() + plan.slice(1),
      planColor: planTierColor(plan, planOrder),
      subscription: m.subscription ?? null,
      status:
        m.onlineStatus === "online" ? "online"
        : m.onlineStatus === "away"  ? "idle"
        : m.onlineStatus === "dnd"   ? "dnd"
        : "offline",
    });
  }
  for (const plan of Object.keys(buckets)) {
    buckets[plan].sort((a, b) => {
      const ta = a.subscription?.subscriptionUpdatedAt?.toMillis?.() ?? 0;
      const tb = b.subscription?.subscriptionUpdatedAt?.toMillis?.() ?? 0;
      return tb - ta;
    });
  }
  const knownPlans   = planOrder.filter((p) => buckets[p]);
  const unknownPlans = Object.keys(buckets).filter((p) => !planOrder.includes(p)).sort();
  return [...knownPlans, ...unknownPlans].map((plan) => ({
    id: plan,
    label: planLabel(plan, buckets[plan].length, plansMap),
    color: planTierColor(plan, planOrder),
    members: buckets[plan],
  }));
}

// ─── Status dot ───────────────────────────────────────────────────────────────

const StatusDot = memo(function StatusDot({ status }) {
  const color = STATUS_COLOR[status] ?? STATUS_COLOR.offline;
  const isOffline = status === "offline";
  return (
    <View
      style={[
        styles.statusDot,
        { backgroundColor: isOffline ? "transparent" : color },
        isOffline && { borderWidth: 1.5, borderColor: color },
      ]}
    />
  );
});

// ─── Avatar helper ────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  "#A259FF", "#4ade80", "#f87171", "#fbbf24",
  "#60a5fa", "#f472b6", "#34d399", "#fb923c",
];

function avatarColor(str = "") {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

// ─── Member row ───────────────────────────────────────────────────────────────

const MemberRow = memo(function MemberRow({ member, onPress }) {
  const [hovered, setHovered] = useState(false);
  const rowRef = useRef(null);
  const isOffline = member.status === "offline";
  const aColor = avatarColor(member.name);

  const webHandlers = useMemo(
    () =>
      Platform.OS === "web"
        ? { onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) }
        : {},
    [],
  );

  const handlePress = useCallback(() => {
    rowRef.current?.measureInWindow((x, y, width, height) => {
      onPress(member, { x, y, width, height });
    });
  }, [member, onPress]);

  return (
    <TouchableOpacity
      ref={rowRef}
      style={[styles.memberRow, hovered && { backgroundColor: HOVER_BG }]}
      activeOpacity={0.8}
      onPress={handlePress}
      {...webHandlers}
    >
      {/* Avatar */}
      <View style={styles.avatarWrap}>
        <View style={[styles.avatar, { backgroundColor: isOffline ? "rgba(255,255,255,0.06)" : aColor, opacity: isOffline ? 0.5 : 1 }]}>
          <Text style={[styles.avatarText, isOffline && { color: TEXT_TER }]}>
            {member.name.charAt(0).toUpperCase()}
          </Text>
        </View>
        <View style={styles.statusBadge}>
          <StatusDot status={member.status} />
        </View>
      </View>

      {/* Info */}
      <View style={styles.memberInfo}>
        <Text
          style={[styles.memberName, { color: isOffline ? TEXT_TER : TEXT_SEC, fontWeight: isOffline ? "400" : "500" }]}
          numberOfLines={1}
        >
          {member.name}
        </Text>
        {!isOffline && member.status !== "online" && (
          <Text style={styles.memberStatus} numberOfLines={1}>
            {member.status === "idle" ? "Away" : member.status === "dnd" ? "Do Not Disturb" : ""}
          </Text>
        )}
      </View>

      {/* Plan color pip */}
      {member.plan !== "free" && (
        <View style={[styles.planPip, { backgroundColor: member.planColor }]} />
      )}
    </TouchableOpacity>
  );
});

// ─── Group header ─────────────────────────────────────────────────────────────

const GroupHeader = memo(function GroupHeader({ group }) {
  return (
    <View style={styles.groupHeader}>
      <View style={[styles.groupDot, { backgroundColor: group.color }]} />
      <Text style={[styles.groupLabel, { color: group.color }]}>{group.label}</Text>
    </View>
  );
});

// ─── Main component ───────────────────────────────────────────────────────────

export default function MembersList({ members: membersProp }) {
  const [planConfig, setPlanConfig] = useState({ order: FALLBACK_PLAN_ORDER, map: {} });
  const [selection, setSelection] = useState(null);
  const handleSelectMember = useCallback((member, position) => setSelection({ member, position }), []);
  const handleCloseCard    = useCallback(() => setSelection(null), []);

  useEffect(() => {
    getPlans()
      .then((plans) => {
        setPlanConfig({
          order: [...plans.map((p) => p.key), "free"],
          map: Object.fromEntries(plans.map((p) => [p.key, p])),
        });
      })
      .catch(() => {});
  }, []);

  const groupedData = useMemo(
    () => (membersProp ? groupMembers(membersProp, planConfig.order, planConfig.map) : []),
    [membersProp, planConfig],
  );

  const onlineCount = useMemo(
    () => membersProp ? membersProp.filter((m) => m.onlineStatus === "online").length : 0,
    [membersProp],
  );

  const totalCount = useMemo(() => membersProp ? membersProp.length : 0, [membersProp]);

  return (
    <View style={styles.sidebar}>
      {/* Header */}
      <View style={styles.header}>
        <Ionicons name="people-outline" size={15} color={BRAND} style={{ marginRight: 8 }} />
        <Text style={styles.headerTitle}>Members</Text>
        <View style={styles.onlineBadge}>
          <View style={[styles.onlineDot]} />
          <Text style={styles.onlineCount}>{onlineCount}</Text>
        </View>
      </View>

      <View style={styles.divider} />

      {/* Member list */}
      <ScrollView
        style={styles.list}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: 12 }}
      >
        {groupedData.length === 0 ? (
          <Text style={styles.emptyText}>No members yet</Text>
        ) : (
          groupedData.map((group) => (
            <View key={group.id}>
              <GroupHeader group={group} />
              {group.members.map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  onPress={handleSelectMember}
                />
              ))}
            </View>
          ))
        )}
      </ScrollView>

      {/* Footer */}
      <View style={styles.footer}>
        <Ionicons name="ellipse" size={8} color={STATUS_COLOR.online} />
        <Text style={styles.footerText}>{onlineCount} online</Text>
        <View style={styles.footerSep} />
        <Ionicons name="ellipse" size={8} color="rgba(255,255,255,0.2)" />
        <Text style={styles.footerText}>{totalCount} total</Text>
      </View>

      {/* Member card popup */}
      <MemberCard
        member={selection?.member ?? null}
        position={selection?.position ?? null}
        onClose={handleCloseCard}
      />
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  sidebar: {
    width: SIDEBAR_W,
    height: "100%",
    backgroundColor: CARD_BG,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: BORDER,
    overflow: "hidden",
    flexDirection: "column",
  },

  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    paddingVertical: 14,
  },
  headerTitle: {
    flex: 1,
    fontSize: 13,
    fontWeight: "700",
    color: TEXT_PRI,
    letterSpacing: 0.1,
  },
  onlineBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    backgroundColor: "rgba(35,165,90,0.15)",
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 10,
  },
  onlineDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: STATUS_COLOR.online,
  },
  onlineCount: {
    fontSize: 11,
    fontWeight: "700",
    color: STATUS_COLOR.online,
  },

  divider: {
    height: 1,
    backgroundColor: BORDER,
  },

  list: {
    flex: 1,
    paddingTop: 6,
  },

  emptyText: {
    fontSize: 12,
    color: TEXT_TER,
    textAlign: "center",
    marginTop: 24,
    paddingHorizontal: 12,
  },

  // Group header
  groupHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingTop: 14,
    paddingBottom: 4,
    gap: 6,
  },
  groupDot: {
    width: 5,
    height: 5,
    borderRadius: 3,
  },
  groupLabel: {
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.6,
    textTransform: "uppercase",
  },

  // Member row
  memberRow: {
    flexDirection: "row",
    alignItems: "center",
    marginHorizontal: 6,
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 8,
    ...(Platform.OS === "web" ? { cursor: "pointer" } : {}),
  },

  // Avatar
  avatarWrap: {
    position: "relative",
    marginRight: 9,
    width: 30,
    height: 30,
  },
  avatar: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: "center",
    justifyContent: "center",
  },
  avatarText: {
    color: "#fff",
    fontSize: 12,
    fontWeight: "700",
  },
  statusBadge: {
    position: "absolute",
    bottom: -1,
    right: -1,
    backgroundColor: "#000",
    borderRadius: 5,
    padding: 1,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },

  // Info
  memberInfo: {
    flex: 1,
    overflow: "hidden",
  },
  memberName: {
    fontSize: 13,
    lineHeight: 16,
  },
  memberStatus: {
    fontSize: 10,
    color: TEXT_TER,
    lineHeight: 13,
    marginTop: 1,
  },

  planPip: {
    width: 5,
    height: 5,
    borderRadius: 3,
    marginLeft: 4,
  },

  // Footer
  footer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: BORDER,
    gap: 5,
  },
  footerText: {
    fontSize: 11,
    color: TEXT_TER,
  },
  footerSep: {
    width: 1,
    height: 10,
    backgroundColor: BORDER,
    marginHorizontal: 2,
  },
});
